import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useScroll from '../../hooks/useScroll';

export default function Navbar({ children }) {
  const { isScrolled, isVisible } = useScroll(50);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/' || location.pathname === '/about' || location.pathname === '/programs' || location.pathname === '/contact';

  // Pages with a dark hero or slider at the top
  const isDarkPageTop = location.pathname === '/business-lab' || location.pathname === '/testimonials' || location.pathname.startsWith('/course');

  const handleNavLinkClick = (e, link) => {
    if (link.path === '/') {
      if (isHome) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsMobileOpen(false);
      }
    } else {
      setIsMobileOpen(false);
    }
  };

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Courses', path: '/courses' },
    { label: 'Junior Academy', path: '/junior-academy' },
    { label: 'Elevare', path: '/elevare' },
    { label: 'Business Lab', path: '/business-lab' },
    { label: 'Testimonials', path: '/testimonials' }
  ];

  return (
    <header
      className={`sticky top-0 w-full z-50 transition-all duration-500 ${isScrolled
        ? 'py-2 md:py-3'
        : 'py-4 md:py-5'
        } ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}
    >
      <nav
        role="navigation"
        aria-label="Main navigation"
        className="w-full"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className={`flex justify-between items-center transition-all duration-500 px-6 py-3 md:py-3.5 rounded-2xl ${isScrolled
            ? 'bg-[#eef2f7] nm-flat-light'
            : ((!isScrolled && isDarkPageTop)
              ? 'bg-[#EFEAD8] nm-flat-dark'
              : 'bg-[#eef2f7] nm-flat-light')
            }`}>
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Back Button (Only on non-home pages) */}
              {!isHome && (
                <button
                  onClick={() => navigate(-1)}
                  className={`group px-2.5 sm:px-3 py-1.5 rounded-xl flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-semibold transition-all duration-300 cursor-pointer ${(!isScrolled && isDarkPageTop)
                    ? 'nm-button-dark text-[#0B1C2D] border-transparent'
                    : 'nm-button-light text-slate-700 border-transparent'
                    }`}
                >
                  <span className="transition-transform duration-200 group-hover:-translate-x-0.5">←</span>
                  <span className="hidden xs:inline">Back</span>
                  <span className="inline xs:hidden">Back</span>
                </button>
              )}

              {/* Logo and Brand */}
              <a href="/" className="flex items-center space-x-2 sm:space-x-3 group" onClick={(e) => handleNavLinkClick(e, { path: '/' })}>
                <img
                  src="/logo-main.webp"
                  alt="Cervello Eduverse Logo"
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-[#C6A25E] shadow-md group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className={`text-base sm:text-xl font-bold bg-clip-text text-transparent transition-all duration-300 bg-gradient-to-r ${(!isScrolled && isDarkPageTop)
                  ? 'from-[#0B1C2D] to-[#C6A25E]'
                  : 'from-[#0B1C2D] to-[#C6A25E]'
                  }`}>
                  Cervello Eduverse
                </div>
              </a>
            </div>

            {/* Desktop Links */}
            <div className="hidden md:flex items-center space-x-2">
              {navLinks.map((link) => {
                const isActive = link.path === '/' ? isHome : location.pathname === link.path;
                return (
                  <a
                    key={link.label}
                    href={link.path}
                    onClick={(e) => handleNavLinkClick(e, link)}
                    className={`px-3.5 py-1.5 rounded-xl text-sm font-semibold transition-all duration-300 ${isActive
                      ? (isScrolled
                        ? 'bg-[#eef2f7] nm-inset-light text-[#C6A25E]'
                        : ((!isScrolled && isDarkPageTop)
                          ? 'bg-[#EFEAD8] nm-inset-dark text-[#C6A25E]'
                          : 'bg-[#eef2f7] nm-inset-light text-[#C6A25E]'))
                      : ((!isScrolled && isDarkPageTop)
                        ? 'text-slate-700 hover:text-[#C6A25E] hover:bg-[#EFEAD8] hover:shadow-[inset_2px_2px_5px_#d8d2be,inset_-2px_-2px_5px_#ffffff]'
                        : 'text-slate-600 hover:text-[#C6A25E] hover:bg-[#eef2f7] hover:shadow-[inset_2px_2px_5px_#d1d9e6,inset_-2px_-2px_5px_#ffffff]')
                      }`}
                  >
                    {link.label}
                  </a>
                );
              })}

              <div className="w-[1px] h-5 bg-slate-200/50 mx-2" />

              <a
                href="/book-demo"
                className={`px-5 py-2.5 rounded-xl font-semibold transition-all duration-300 text-sm ${(!isScrolled && isDarkPageTop)
                  ? 'nm-button-dark text-[#0B1C2D] border-transparent'
                  : 'nm-button-light text-[#0B1C2D] border-transparent'
                  }`}
              >
                Book Demo
              </a>
            </div>

            {/* Mobile Toggle Button */}
            <button
              className={`md:hidden p-2 rounded-xl transition-all focus:outline-none ${(!isScrolled && isDarkPageTop) ? 'nm-button-dark text-[#0B1C2D]' : 'nm-button-light text-[#0B1C2D]'
                }`}
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              aria-expanded={isMobileOpen}
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>

          {children}

          {/* Floating Mobile Drawer Dropdown */}
          {isMobileOpen && (
            <div className="md:hidden absolute top-full left-4 right-4 mt-2 bg-[#eef2f7] nm-flat-light rounded-2xl py-4 px-6 z-50 animate-slide-up">
              <div className="flex flex-col space-y-2">
                {navLinks.map((link) => {
                  const isActive = link.path === '/' ? isHome : location.pathname === link.path;
                  return (
                    <a
                      key={link.label}
                      href={link.path}
                      onClick={(e) => handleNavLinkClick(e, link)}
                      className={`py-2 px-3 rounded-xl text-base font-semibold transition-all border ${isActive
                        ? 'bg-[#eef2f7] nm-inset-light text-[#C6A25E] border-transparent'
                        : 'text-[#1E293B] hover:text-[#C6A25E] hover:bg-[#eef2f7] hover:shadow-[inset_2px_2px_5px_#d1d9e6,inset_-2px_-2px_5px_#ffffff] border-transparent'
                        }`}
                    >
                      {link.label}
                    </a>
                  );
                })}
                <a
                  href="/book-demo"
                  className="bg-[#eef2f7] nm-button-light text-[#0B1C2D] py-3 rounded-xl font-semibold text-center shadow-md hover:scale-[1.01] transition-transform mt-3"
                  onClick={() => setIsMobileOpen(false)}
                >
                  Book Demo
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
